import React, { useCallback, useRef, useState } from 'react';
import css from './SignupForm.module.css';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { SharetribeApi } from '../../../util/api';
import { required } from '../../../util/validators';
import { StatusCode } from '../../../util/enums';

const UserNameField = ({ intl, formId }) => {
  const [usernameAvailable, setUsernameAvailable] = useState(null); // Use null as initial state to indicate "not checked"
  const [hasCheckedUsername, setHasCheckedUsername] = useState(false);

  const useDebounce = (func, delay) => {
    const timerRef = useRef(null);

    const debouncedFunction = useCallback(
      (...args) => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          func(...args);
        }, delay);
      },
      [func, delay]
    );

    return debouncedFunction;
  };

  const handleSearchChange = useCallback(async value => {
    // Skip API call if the value is empty
    if (!value || value.trim() === '') {
      setHasCheckedUsername(false);
      setUsernameAvailable(null);
      return;
    }

    setHasCheckedUsername(false); // Reset the check state on input change
    try {
      const response = await SharetribeApi.checkUsernameAvailability({ username: value });
      setHasCheckedUsername(true); // Set the check state to true after the response
      if (response.statusCode === StatusCode.SUCCESS) {
        setUsernameAvailable(true);
      } else {
        setUsernameAvailable(false);
      }
    } catch (error) {
      setHasCheckedUsername(true); // Set the check state to true even if there's an error
      setUsernameAvailable(false);
    }
  }, []);

  const debouncedSearch = useDebounce(handleSearchChange, 300);

  // Custom validation function for username
  const validateUsername = value => {
    // First, check if the field is required
    const requiredError = required(
      intl.formatMessage({ id: 'SignupForm.FanProfileFormFields.usernameRequired' })
    )(value);

    if (requiredError) {
      return requiredError; // Return required error if the field is empty
    }

    // If the username has been checked and is not available, return an error
    if (hasCheckedUsername && !usernameAvailable) {
      return intl.formatMessage({
        id: 'SignupForm.FanProfileFormFields.usernameNotAvailable',
      });
    }

    // No errors
    return undefined;
  };
  return (
    <div className={classNames(css.formRow, css.fullWidth)}>
      <div className={css.formFld}>
        <label className={css.label}>
          {intl.formatMessage({ id: 'SignupForm.FanProfileFormFields.username' })}
        </label>
        <Field
          id={formId ? `${formId}.username` : 'username'}
          name="username"
          type="text"
          validate={validateUsername}
          label="Username"
          render={({ input, meta }) => (
            <div className={css.errorMsg}>
              <input
                {...input}
                type="text"
                onChange={e => {
                  input.onChange(e);
                  debouncedSearch(e.target.value);
                }}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.FanProfileFormFields.usernamePlaceholder',
                })}
              />
              {/* Show validation error if the field is touched and has an error */}
              {meta.touched && meta.error && <span>{meta.error}</span>}
              {/* Show availability message only if the username has been checked */}
              {hasCheckedUsername && usernameAvailable !== null && (
                <span>
                  {usernameAvailable
                    ? intl.formatMessage({
                        id: 'SignupForm.FanProfileFormFields.usernameAvailable',
                      })
                    : intl.formatMessage({
                        id: 'SignupForm.FanProfileFormFields.usernameNotAvailable',
                      })}
                </span>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default UserNameField;
